// A HITELESÍTÉS-ELŐTTI oldalak közös burka (login · elfelejtett jelszó · jelszó beállítása).
//
// MIÉRT külön komponens: a Claude Design „Atlas" terv login-képernyője (docs/foundations/design.md) egy elég összetett
// két-hasábos layout (sötét vizuál-panel rács-háttérrel + zöld derengéssel, headline, statisztikák), és a
// jelszó-visszaállító folyamat KÉT új oldala pontosan ugyanezt a design-nyelvet használja. Három példányban ez
// ~60 sor háromszor duplikált inline-gradiens lenne, ahol az első design-hangolás után azonnal elcsúsznak.
//
// A burok CSAK a vizuális keretet adja; a tartalom (címsor + űrlap) a gyermek dolga. A branding mobilon a
// tartalom fölött jelenik meg, desktopon a bal panelen (nincs duplikáció).
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AtlasMark, AtlasWordmark } from '@/components/brand/atlas-logo';

interface AuthShellProps {
  /** Az oldal címsora (h1). */
  title: string;
  /** Rövid alcím a címsor alatt. */
  subtitle: string;
  /** Az űrlap / tartalom. */
  children: ReactNode;
  /** Opcionális lábjegyzet a kártya alatt (pl. „Vissza a bejelentkezéshez"). */
  footer?: ReactNode;
}

export function AuthShell({ title, subtitle, children, footer }: AuthShellProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <main className="bg-background app-viewport app-safe-area app-safe-bottom grid w-screen overflow-y-auto lg:grid-cols-2">
      {/* Tartalom — desktopon JOBB oldalon (a vizuál-panel balra) */}
      <div className="flex items-center justify-center p-8 lg:order-2">
        <div className="animate-in fade-in slide-in-from-bottom-1 w-full max-w-[360px] duration-300">
          {/* Branding CSAK mobilon (desktopon a bal vizuál-panel viszi — nincs duplikáció) */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <AtlasMark size={32} />
            <AtlasWordmark className="text-[22px]" />
          </div>

          <h1 className="mb-2 text-[26px] font-semibold tracking-[-0.015em]">{title}</h1>
          <p className="text-muted-foreground mb-8 text-base">{subtitle}</p>

          {children}

          {footer !== undefined && (
            <div className="text-muted-foreground mt-6 text-center text-sm">{footer}</div>
          )}
        </div>
      </div>

      {/* Zöld brand vizuál-panel — desktopon BAL oldalon (≥lg) — a DocuShare hero-mintája:
          lineáris zöld gradiens alap (accent-grad-from→to) + zöld radiálok + accent-700 overlay + rács. */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:order-1 lg:flex">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 20%, rgb(var(--accent-500) / 0.35), transparent 45%), radial-gradient(circle at 85% 75%, rgb(var(--accent-300) / 0.3), transparent 50%), linear-gradient(135deg, var(--accent-grad-from), var(--accent-grad-to))',
          }}
        />
        <div className="auth-grid-bg" />
        <div className="from-accent-700/90 via-accent-700/75 to-accent-600/60 pointer-events-none absolute inset-0 bg-gradient-to-br" />

        <div className="relative z-10 flex items-center gap-3">
          <AtlasMark size={32} />
          <span className="text-[22px] font-semibold">Atlas</span>
        </div>

        <div className="relative z-10">
          {/* A címke CSAK a rendszer jellegét mondja el. A verzió-jelölés („v2.0 beta") KIVEZETVE: egy
              kijelentkezett, publikus lapon a belső verziószám nem üzenet, csak zaj — és avulni is tud
              (senki nem frissíti a login-lapot egy release-nél). */}
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/50">
            {t('login.heroEyebrow')}
          </div>
          <h2 className="mb-4 text-4xl font-semibold leading-[1.1] tracking-[-0.02em]">
            {t('login.heroLine1')}
            <br />
            {t('login.heroLine2')}
          </h2>
          {/* A STATISZTIKA-blokk (aktív ügyfél / munkatárs / év tapasztalat) KIVEZETVE: fix, kézzel írt
              számok voltak egy publikus lapon — marketing-hangvétel egy belsős belépőn, ráadásul azonnal
              avuló adat (nem élő mérés). A panel innentől a rendszer NEVÉT és a funkció-körét mondja el. */}
          <p className="max-w-[440px] text-[15px] leading-relaxed text-white/70">
            {t('login.heroBody')}
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/40">© 2026 MORGENS Design Kft.</div>
      </div>
    </main>
  );
}
